import { IData } from "../../types/deviceInterface";
import { IField } from "../../types/fieldInterface";

type RGBTriplet = [number, number, number];

export const MOISTURE_INDEX_RED = "rgb(255, 0, 0)";
export const MOISTURE_INDEX_DARK_RED = "rgb(180, 0, 0)";
export const MOISTURE_INDEX_ORANGE = "rgb(255, 165, 0)";
export const MOISTURE_INDEX_GREEN = "rgb(68, 255, 68)";
export const MOISTURE_INDEX_LIGHT_BLUE = "rgb(68, 255, 250)";
export const MOISTURE_INDEX_DARK_BLUE = "rgb(0, 0, 250)";
export const MOISTURE_INDEX_RED_TRANSPARENT = "rgba(255, 0, 0, 0)";
export const MOISTURE_INDEX_DARK_BLUE_TRANSPARENT = "rgba(0, 0, 250, 0)";
export const MOISTURE_INDEX_LEGEND_MIN = -0.1;
export const MOISTURE_INDEX_LEGEND_MAX = 1.1;

export interface MoistureStatusThresholds {
  moisture_limit_critical?: number;
  moisture_limit_damage?: number;
  moisture_limit_deficit?: number;
  moisture_limit_field_capacity?: number;
}

export interface MoistureIndexThresholds {
  management?: number;
  deficit?: number;
  upperManagement?: number;
}

export interface MoistureIndexSegment {
  from: number;
  to: number;
  color: string;
  gradientStartColor?: string;
  gradientEndColor?: string;
  regionLabel: string;
}

function blend(c1: RGBTriplet, c2: RGBTriplet, ratio: number): RGBTriplet {
  return c1.map((c, i) => Math.round(c + ratio * (c2[i] - c))) as RGBTriplet;
}

function toThreeStepGradient(
  value: number | undefined,
  params: Record<string, number>,
  low: RGBTriplet,
  mid: RGBTriplet,
  high: RGBTriplet,
): string {
  if (value == null) {
    return "#000";
  }

  const step = Math.min(Math.max((value - params.min) / (params.max - params.min), 0), 1);
  const color = step <= 0.5 ? blend(low, mid, step * 2) : blend(mid, high, (step - 0.5) * 2);
  return `rgb(${color[0]},${color[1]},${color[2]})`;
}

export function moistureColorFn(value: number | undefined, params: Record<string, number>): string {
  return toThreeStepGradient(value, params, [255, 0, 0], [0, 255, 0], [0, 0, 255]);
}

export function conductivityColorFn(
  value: number | undefined,
  params: Record<string, number>,
): string {
  return toThreeStepGradient(value, params, [255, 0, 0], [255, 165, 0], [0, 255, 0]);
}

export function temperatureColorFn(
  value: number | undefined,
  params: Record<string, number>,
): string {
  return toThreeStepGradient(value, params, [0, 0, 255], [255, 255, 255], [255, 0, 0]);
}

export function getColorFunction(pk: keyof IData) {
  if (pk === "soil_conductivity" || pk === "soil_conductivity_2" || pk === "soil_conductivity_3") {
    return conductivityColorFn;
  }
  if (pk === "air_temperature" || pk === "air_temperature_2" || pk === "soil_temperature") {
    return temperatureColorFn;
  }
  return moistureColorFn;
}

export function moistureStatusColorFn(
  value: number | undefined,
  params?: MoistureStatusThresholds,
): string {
  const critical = params?.moisture_limit_critical;
  const damage = params?.moisture_limit_damage;
  const deficit = params?.moisture_limit_deficit;
  const fieldCapacity = params?.moisture_limit_field_capacity;

  if (
    value === undefined ||
    critical === undefined ||
    damage === undefined ||
    deficit === undefined ||
    fieldCapacity === undefined
  ) {
    return "#000";
  }

  if (value <= critical) return "black";
  if (value <= damage) return "red";
  if (value <= deficit) return MOISTURE_INDEX_ORANGE;
  if (value <= fieldCapacity) return "rgb(0, 255, 0)";
  return "blue";
}

export function moistureIndexColorFn(
  value: number | undefined,
  params?: MoistureIndexThresholds,
): string {
  if (value === undefined) {
    return "#000";
  }

  const management = params?.management;
  const deficit = params?.deficit;
  const upperManagement = params?.upperManagement;

  if (typeof management !== "number") {
    return value <= 0.5
      ? MOISTURE_INDEX_RED
      : value <= 1
        ? MOISTURE_INDEX_GREEN
        : MOISTURE_INDEX_DARK_BLUE;
  }

  if (typeof deficit === "number") {
    if (value < deficit) return MOISTURE_INDEX_RED;
    if (value < management) return MOISTURE_INDEX_ORANGE;
  } else if (value < management) {
    return MOISTURE_INDEX_RED;
  }

  if (typeof upperManagement === "number" && value < upperManagement) {
    return MOISTURE_INDEX_GREEN;
  }

  if (value <= 1) {
    return typeof upperManagement === "number" ? MOISTURE_INDEX_LIGHT_BLUE : MOISTURE_INDEX_GREEN;
  }

  return MOISTURE_INDEX_DARK_BLUE;
}

export function getMoistureIndexSegments(
  thresholds: MoistureIndexThresholds,
): MoistureIndexSegment[] {
  const management = thresholds.management;
  const deficit = thresholds.deficit;
  const upperManagement = thresholds.upperManagement;

  if (management === undefined) {
    return [
      {
        from: MOISTURE_INDEX_LEGEND_MIN,
        to: 0,
        color: MOISTURE_INDEX_DARK_RED,
        gradientStartColor: MOISTURE_INDEX_RED_TRANSPARENT,
        regionLabel: "PWP",
      },
      { from: 0, to: 0.5, color: MOISTURE_INDEX_RED, regionLabel: "Damage" },
      { from: 0.5, to: 1, color: MOISTURE_INDEX_GREEN, regionLabel: "Optimal" },
      {
        from: 1,
        to: MOISTURE_INDEX_LEGEND_MAX,
        color: MOISTURE_INDEX_DARK_BLUE,
        gradientEndColor: MOISTURE_INDEX_DARK_BLUE_TRANSPARENT,
        regionLabel: "Runoff",
      },
    ];
  }

  const segments: MoistureIndexSegment[] = [];
  segments.push({
    from: MOISTURE_INDEX_LEGEND_MIN,
    to: 0,
    color: MOISTURE_INDEX_DARK_RED,
    gradientStartColor: MOISTURE_INDEX_RED_TRANSPARENT,
    regionLabel: "PWP",
  });

  const lowerLimit = deficit ?? management;
  if (lowerLimit > 0) {
    segments.push({ from: 0, to: lowerLimit, color: MOISTURE_INDEX_RED, regionLabel: "Damage" });
  }
  if (deficit !== undefined && deficit < management) {
    segments.push({
      from: deficit,
      to: management,
      color: MOISTURE_INDEX_ORANGE,
      regionLabel: "Deficit",
    });
  }
  if (upperManagement !== undefined && upperManagement > management) {
    segments.push({
      from: management,
      to: upperManagement,
      color: MOISTURE_INDEX_GREEN,
      regionLabel: "Optimal",
    });
    if (upperManagement < 1) {
      segments.push({
        from: upperManagement,
        to: 1,
        color: MOISTURE_INDEX_LIGHT_BLUE,
        regionLabel: "High",
      });
    }
  } else if (management < 1) {
    segments.push({ from: management, to: 1, color: MOISTURE_INDEX_GREEN, regionLabel: "Optimal" });
  }
  segments.push({
    from: 1,
    to: MOISTURE_INDEX_LEGEND_MAX,
    color: MOISTURE_INDEX_DARK_BLUE,
    gradientEndColor: MOISTURE_INDEX_DARK_BLUE_TRANSPARENT,
    regionLabel: "Runoff",
  });

  return segments.filter((s) => s.to > s.from);
}

export function formatThresholdLabel(value: number): string {
  return value.toFixed(2);
}

const normalizeThreshold = (value?: number | null) => {
  if (typeof value !== "number") {
    return undefined;
  }
  return value / 100;
};

const clampThreshold = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

export function getMoistureIndexThresholds(field?: IField | null): MoistureIndexThresholds {
  const management = normalizeThreshold(field?.crop_type_details?.management_allowable_depletion);
  const deficit = normalizeThreshold(field?.crop_type_details?.deficit_allowable_depletion);
  const upperManagement = normalizeThreshold(field?.crop_type_details?.upper_management_limit);

  if (management === undefined) {
    return {};
  }

  const normalizedManagement = clampThreshold(management, 0, 1);

  return {
    management: normalizedManagement,
    deficit: deficit === undefined ? undefined : clampThreshold(deficit, 0, normalizedManagement),
    upperManagement:
      upperManagement === undefined
        ? undefined
        : clampThreshold(upperManagement, normalizedManagement, 1),
  };
}
